import { Box, Grid } from '@atgfr8/atg-ui-tailwind';
import {
  Button,
  Notification,
  SelectInput,
  Tab,
  TabsComponent,
} from '@components';
import { AtgLoader } from '@components/AtgLoader';
import {
  deleteFuelSurchargeData,
  fuelSurchargeStore,
  getFuelSurchargeData,
  saveFuelSurcharge,
  setFuelSurchargeStore,
} from '@store/customers/customerDetails/fuelSurcharge';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { isAdmin } from '@utils/utils';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { cloneDeep } from 'lodash';
import { createEffect, createSignal, onMount, Show } from 'solid-js';
import { produce } from 'solid-js/store';

import {
  DefaultFuelSurchargeRange,
  effectiveDays,
  regions,
  types,
} from './constants';
import { SurchargeTable } from './SurchargeTables';

type FuelProps = {
  id: number;
  type: string;
  deleteChangeTrackingSource?: ChangeTrackingSource;
  saveChangeTrackingSource?: ChangeTrackingSource;
};

const FuelSurcharge = (props: FuelProps) => {
  const [activeTab, setActiveTab] = createSignal<number>(0);
  const isDisabled = () => {
    return (
      fuelSurchargeStore.isLoading ||
      (fuelSurchargeStore.fuelSurchargeData?.isDefault ?? false) ||
      !isAdmin()
    );
  };

  onMount(async () => {
    if (props.id) {
      await getFuelSurchargeData(Number(props.id), false, false, props.type);
    }
    setFuelSurchargeStore('errors', []);
  });

  let previousId = props.id;
  createEffect(async () => {
    if (previousId !== props.id) {
      previousId = props.id;
      await getFuelSurchargeData(Number(props.id), false, false, props.type);
    }
  });

  const handleUseDefaultClick = async () => {
    setFuelSurchargeStore('isLoading', true);
    await deleteFuelSurchargeData(
      Number(props.id),
      props.type,
      props.deleteChangeTrackingSource,
    );
    setFuelSurchargeStore('isLoading', false);
  };

  const handleOverrideDefaultClick = async () => {
    setFuelSurchargeStore('isLoading', true);
    await getFuelSurchargeData(Number(props.id), true, true, props.type);
    setFuelSurchargeStore('isLoading', false);
  };

  const handleSaveClick = async () => {
    setFuelSurchargeStore(
      produce((draft) => {
        draft.errors = [];
        draft.isLoading = true;
      }),
    );
    const data = cloneDeep(fuelSurchargeStore.fuelSurchargeData);
    data?.fuelSurchargeRanges.forEach((item) => {
      if (
        ['', null].includes(item.min.toString()) ||
        ['', null].includes(item.max.toString()) ||
        ['', null].includes(item.surchargeModifier.toString())
      ) {
        setFuelSurchargeStore('errors', [
          'Min,Max and Surcharge values are required for range',
        ]);
      }
    });
    if (fuelSurchargeStore.errors.length > 0) {
      setFuelSurchargeStore('isLoading', false);
      return;
    }
    if (data) {
      data.operationType = 'Update';
      data.fuelSurchargeRanges = data.fuelSurchargeRanges.map((item) => {
        if (item.type === types.LTL) {
          item.surchargeModifier = item.surchargeModifier / 100;
        }
        return item;
      });
      await saveFuelSurcharge(
        Number(props.id),
        data,
        props.type,
        props.saveChangeTrackingSource,
      );
    }
    setFuelSurchargeStore('isLoading', false);
  };

  const handleAddNewItem = () => {
    if (!isAdmin()) return;
    const fuelSurcharge = { ...DefaultFuelSurchargeRange };
    fuelSurcharge.type = activeTab();
    setFuelSurchargeStore('fuelSurchargeData', 'fuelSurchargeRanges', [
      ...(fuelSurchargeStore.fuelSurchargeData?.fuelSurchargeRanges || []),
      { ...fuelSurcharge, operationType: 'Insert' },
    ]);
    const table = document.getElementById('fuel-surcharge-table');
    table?.scrollTo(0, table.scrollHeight);
  };

  return (
    <div class="min-h-[400px] h-[calc(100vh-210px)] overflow-scroll">
      <Show when={fuelSurchargeStore.isLoading}>
        <Box class="absolute top-[50%] left-[40%] z-50">
          <AtgLoader />
        </Box>
      </Show>
      <Show when={fuelSurchargeStore.errors.length > 0}>
        <Notification
          type="error"
          text={
            <ul>
              {fuelSurchargeStore.errors.map((error) => (
                <li>{error}</li>
              ))}
            </ul>
          }
          sxProps="mb-4!"
        />
      </Show>
      <div>
        <Grid
          container
          class="border border-gray-300 rounded-md px-2 py-3 items-center m-0! w-full!"
          spacing={2}
        >
          <Grid item size={{ sm: 12, md: 6 }} class="pt-0!">
            <SelectInput
              label="Region"
              value={fuelSurchargeStore.fuelSurchargeData?.eiaRegion ?? ''}
              onChange={(value) => {
                setFuelSurchargeStore('fuelSurchargeData', 'eiaRegion', value);
              }}
              menuItems={regions.map((r) => ({ label: r, value: r }))}
              disabled={isDisabled()}
              dataTestId={formatDataTestId('Region', 'selectInput')}
            />
          </Grid>
          <Grid item size={{ sm: 12, md: 3 }} class="pt-0!">
            <SelectInput
              label="Effective Day"
              value={
                fuelSurchargeStore.fuelSurchargeData?.effectiveDay?.toString() ??
                ''
              }
              onChange={(value) => {
                setFuelSurchargeStore(
                  'fuelSurchargeData',
                  'effectiveDay',
                  Number(value),
                );
              }}
              menuItems={effectiveDays.map((d) => ({
                label: d.label,
                value: String(d.value),
              }))}
              disabled={isDisabled()}
              dataTestId={formatDataTestId('Effective Day', 'selectInput')}
            />
          </Grid>
          <Grid item size={{ sm: 12, md: 3 }} class="flex! pt-0!">
            <Button
              variant="contained"
              label={
                (fuelSurchargeStore.fuelSurchargeData?.isDefault ?? true)
                  ? 'OVERRIDE DEFAULTS'
                  : 'Use Defaults'
              }
              class="w-full"
              onClick={async () => {
                if (fuelSurchargeStore.fuelSurchargeData?.isDefault ?? true) {
                  await handleOverrideDefaultClick();
                } else {
                  await handleUseDefaultClick();
                }
              }}
              sx={{ width: 'fit-content' }}
              disabled={!isAdmin()}
              dataTestId={formatDataTestId(
                'OVERRIDE DEFAULTS Use Defaults',
                'button',
              )}
            />
            <Button
              variant="contained"
              label="SAVE"
              class="w-full ml-4!"
              onClick={handleSaveClick}
              sx={{ width: 'fit-content' }}
              disabled={isDisabled()}
              dataTestId={formatDataTestId('SAVE', 'button')}
            />
          </Grid>
        </Grid>
      </div>
      <TabsComponent
        tabs={['LTL', 'TRUCK LOAD'].map((item) => (
          <Tab>{item}</Tab>
        ))}
        tabStyleClasses="ml-2 mt-4"
        activeTab={activeTab()}
        onTabClick={(i) => {
          setActiveTab(i);
        }}
      />
      <div>
        <SurchargeTable
          activeTab={activeTab()}
          isDefault={fuelSurchargeStore.fuelSurchargeData?.isDefault ?? true}
          addNewItem={handleAddNewItem}
        />
      </div>
    </div>
  );
};

export default FuelSurcharge;
